import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MapPin, Users, Ruler } from 'lucide-react';
import axios from 'axios';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Listings() {
  usePageTitle('Yacht Listings');
  
  const [yachts, setYachts] = useState([]);
  const [filteredYachts, setFilteredYachts] = useState([]);
  const [filters, setFilters] = useState({
    yacht_type: '',
    listing_type: '',
    location: '',
    min_length: '',
    max_length: '',
  });

  useEffect(() => {
    fetchYachts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, yachts]);

  const fetchYachts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts`);
      setYachts(response.data);
      setFilteredYachts(response.data);
    } catch (error) {
      console.error('Error fetching yachts:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...yachts];

    if (filters.yacht_type) {
      filtered = filtered.filter((y) => y.yacht_type === filters.yacht_type);
    }
    if (filters.listing_type) {
      filtered = filtered.filter((y) => y.listing_type === filters.listing_type);
    }
    if (filters.location) {
      filtered = filtered.filter((y) => y.location.toLowerCase().includes(filters.location.toLowerCase()));
    }
    if (filters.min_length) {
      filtered = filtered.filter((y) => y.length_ft >= parseFloat(filters.min_length));
    }
    if (filters.max_length) {
      filtered = filtered.filter((y) => y.length_ft <= parseFloat(filters.max_length));
    }

    setFilteredYachts(filtered);
  };

  const resetFilters = () => {
    setFilters({
      yacht_type: '',
      listing_type: '',
      location: '',
      min_length: '',
      max_length: '',
    });
  };

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto pb-24">
      {/* Header */}
      <div className="mb-16">
        <h1
          className="text-5xl md:text-6xl font-medium tracking-tight mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="listings-title"
        >
          Yacht <span className="text-gold">Collection</span>
        </h1>
        <p className="text-lg text-white/60 max-w-2xl">
          Explore our curated selection of luxury yachts available for charter and purchase across Southeast Asia.
        </p>
      </div>

      {/* Filters */}
      <div className="glass-light p-8 mb-12" data-testid="filters-section">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Select value={filters.yacht_type} onValueChange={(value) => setFilters({ ...filters, yacht_type: value })}>
            <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="filter-yacht-type">
              <SelectValue placeholder="Yacht Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A192F] border-white/20 text-white">
              <SelectItem value="superyacht">Superyacht</SelectItem>
              <SelectItem value="catamaran">Catamaran</SelectItem>
              <SelectItem value="power">Power Yacht</SelectItem>
              <SelectItem value="sail">Sailing Yacht</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.listing_type} onValueChange={(value) => setFilters({ ...filters, listing_type: value })}>
            <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="filter-listing-type">
              <SelectValue placeholder="Listing Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A192F] border-white/20 text-white">
              <SelectItem value="charter">Charter</SelectItem>
              <SelectItem value="sale">For Sale</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="Location"
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            className="bg-transparent border-white/20 text-white placeholder:text-white/40"
            data-testid="filter-location"
          />

          <Input
            placeholder="Min Length (ft)"
            type="number"
            value={filters.min_length}
            onChange={(e) => setFilters({ ...filters, min_length: e.target.value })}
            className="bg-transparent border-white/20 text-white placeholder:text-white/40"
            data-testid="filter-min-length"
          />

          <Input
            placeholder="Max Length (ft)"
            type="number"
            value={filters.max_length}
            onChange={(e) => setFilters({ ...filters, max_length: e.target.value })}
            className="bg-transparent border-white/20 text-white placeholder:text-white/40"
            data-testid="filter-max-length"
          />
        </div>

        <div className="mt-4 flex justify-between items-center">
          <p className="text-white/60 text-sm">
            Showing {filteredYachts.length} of {yachts.length} yachts
          </p>
          <Button
            onClick={resetFilters}
            variant="ghost"
            className="text-gold hover:text-gold/80 text-sm"
            data-testid="reset-filters-btn"
          >
            Reset Filters
          </Button>
        </div>
      </div>

      {/* Yacht Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="yacht-grid">
        {filteredYachts.map((yacht, idx) => (
          <Link
            key={yacht.yacht_id}
            to={`/yacht/${yacht.yacht_id}`}
            className={`group relative overflow-hidden glass-light hover:border-gold/30 transition-all duration-500 ${
              idx === 0 ? 'md:col-span-2 md:row-span-2 h-[700px]' : 'h-[500px]'
            }`}
            data-testid={`yacht-listing-${yacht.yacht_id}`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
              style={{ backgroundImage: `url('${yacht.images[0]}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent"></div>
            </div>
            <div className="relative h-full flex flex-col justify-end p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs uppercase tracking-[0.2em] text-gold">{yacht.builder}</span>
                <span className="text-white/40">•</span>
                <span className="text-xs uppercase tracking-wide text-white/60">{yacht.listing_type}</span>
              </div>
              <h3
                className={`font-medium mb-3 ${idx === 0 ? 'text-4xl' : 'text-2xl'}`}
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {yacht.name}
              </h3>
              <div className="flex items-center gap-4 text-sm text-white/60 mb-4">
                <div className="flex items-center gap-1">
                  <Ruler size={14} />
                  <span>{yacht.length_ft}ft</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Users size={14} />
                  <span>{yacht.guest_capacity} guests</span>
                </div>
                <span>•</span>
                <span className="capitalize">{yacht.yacht_type}</span>
              </div>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <MapPin size={14} />
                <span>{yacht.location}</span>
              </div>
              <p className="text-gold font-semibold text-lg">
                {yacht.charter_weekly_usd
                  ? `$${yacht.charter_weekly_usd.toLocaleString()} / week`
                  : `$${yacht.price_usd?.toLocaleString()}`}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {filteredYachts.length === 0 && (
        <div className="text-center py-20" data-testid="no-yachts-message">
          <p className="text-white/60 text-lg">No yachts match your current filters. Try adjusting them.</p>
        </div>
      )}
    </div>
  );
}
